import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { apiFetch } from '../lib/api'
import { usePreferences } from '../hooks/usePreferences'
import type { AuditLog } from '../types'

const ACTIONS = ['create', 'update', 'delete', 'view', 'export', 'login', 'settings']
const ENTITY_TYPES = ['camera', 'site', 'gateway', 'user', 'organization', 'layout', 'recording', 'event']

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-success/10 text-success',
  update: 'bg-info/10 text-info',
  delete: 'bg-error/10 text-error',
  view: 'bg-gray-100 text-gray-600',
  export: 'bg-purple-100 text-purple-700',
  login: 'bg-brand/10 text-brand',
  settings: 'bg-warning/10 text-warning',
}

export function AuditLogPage() {
  const { profile } = useAuth()
  const { formatDate } = usePreferences()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [actionFilter, setActionFilter] = useState('')
  const [entityFilter, setEntityFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const pageSize = 50

  const orgId = profile?.org_id

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const params = new URLSearchParams({
        orgId,
        limit: String(pageSize),
        offset: String(page * pageSize),
      })
      if (actionFilter) params.set('action', actionFilter)
      if (entityFilter) params.set('entity_type', entityFilter)
      if (fromDate) params.set('from', `${fromDate}T00:00:00`)
      if (toDate) params.set('to', `${toDate}T23:59:59`)

      const res = await apiFetch<{ logs: AuditLog[]; total: number }>(
        `/api/audit-logs?${params}`,
      )
      setLogs(res.logs ?? [])
      setTotal(res.total ?? 0)
    } catch {
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [orgId, page, actionFilter, entityFilter, fromDate, toDate])

  useEffect(() => { load() }, [load])

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-gray-900">Audit Log</h1>
        <p className="text-xs text-gray-400">{total} entries</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(0) }}
          className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700"
        >
          <option value="">All Actions</option>
          {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={entityFilter}
          onChange={(e) => { setEntityFilter(e.target.value); setPage(0) }}
          className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700"
        >
          <option value="">All Types</option>
          {ENTITY_TYPES.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(0) }}
          className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700"
          placeholder="From"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(0) }}
          className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700"
          placeholder="To"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Time</th>
              <th className="text-left px-4 py-2.5 font-medium">User</th>
              <th className="text-left px-4 py-2.5 font-medium">Action</th>
              <th className="text-left px-4 py-2.5 font-medium">Type</th>
              <th className="text-left px-4 py-2.5 font-medium">Name</th>
              <th className="text-left px-4 py-2.5 font-medium">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No audit entries found</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                  {formatDate(log.created_at)}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[180px] truncate">
                  {log.user_email ?? 'System'}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${ACTION_COLORS[log.action] ?? 'bg-gray-100 text-gray-600'}`}>
                    {log.action}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-600">{log.entity_type}</td>
                <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[200px] truncate">
                  {log.entity_name ?? log.entity_id ?? '-'}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-400 max-w-[200px] truncate font-mono">
                  {log.details && Object.keys(log.details).length > 0
                    ? JSON.stringify(log.details)
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="text-xs text-gray-500 hover:text-gray-900 disabled:opacity-30"
          >
            Previous
          </button>
          <span className="text-xs text-gray-400">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="text-xs text-gray-500 hover:text-gray-900 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
